import { callStatuses, audioDeviceErrorMessages, defaultCallValues } from "@/src/constants/callConstants";
import store from "@/src/store";

class CampaignCallHandler {
  extractCampaignId(extraHeaders) {
    try {
      if (!extraHeaders || typeof extraHeaders !== "object") {
        return null;
      }

      const campaignIdKey = Object.keys(extraHeaders).find(
        (key) => key && key.toLowerCase() === "x-ph-campaignid"
      );

      if (!campaignIdKey) {
        return null;
      }

      const campaignId = extraHeaders[campaignIdKey];
      
      if (campaignId == null) {
        return null;
      }

      return campaignId;
    } catch (error) {
      console.error("[CampaignCallHandler] Error extracting campaign ID:", error);
      return null;
    }
  }

  isCampaignCall(campaignId) {
    try {
      if (campaignId == null) {
        return false;
      }

      const campaignIdStr = String(campaignId).trim();

      if (campaignIdStr === "" || campaignIdStr === "0") {
        return false;
      }

      return true;
    } catch (error) {
      console.error("[CampaignCallHandler] Error checking campaign call:", error);
      return false;
    }
  }

  shouldAutoAnswer(extraHeaders) {
    try {
      const campaignId = this.extractCampaignId(extraHeaders);
      return this.isCampaignCall(campaignId);
    } catch (error) {
      console.error("[CampaignCallHandler] Error checking auto-answer:", error);
      return false;
    }
  }

  createCampaignCallDetails(srcNumber, destEndpoint, campaignId) {
    try {
      const parsedCampaignId = campaignId ? parseInt(campaignId, 10) : defaultCallValues.DEFAULT_CAMPAIGN_ID;

      return {
        name: srcNumber || defaultCallValues.CAMPAIGN_CALL_NAME,
        number: srcNumber || "",
        From: srcNumber || "",
        To: destEndpoint || "",
        status: callStatuses.CONNECTING,
        campaignId: isNaN(parsedCampaignId) ? defaultCallValues.DEFAULT_CAMPAIGN_ID : parsedCampaignId,
      };
    } catch (error) {
      console.error("[CampaignCallHandler] Error creating call details:", error);
      return {
        name: defaultCallValues.CAMPAIGN_CALL_NAME,
        number: "",
        From: "",
        To: "",
        status: callStatuses.CONNECTING,
        campaignId: defaultCallValues.DEFAULT_CAMPAIGN_ID,
      };
    }
  }

  async updateStoreForCampaignCall(provider, campaignId, callDetails) {
    try {
      if (!provider || !campaignId || !callDetails) {
        console.error("[CampaignCallHandler] Invalid parameters for store update");
        return false;
      }

      const parsedCampaignId = parseInt(campaignId, 10);
      
      if (isNaN(parsedCampaignId)) {
        console.error("[CampaignCallHandler] Invalid campaign ID for store update");
        return false;
      }
      store.commit("telephony/setProvider", provider);
      await store.dispatch("calls/updateCallStatus", callStatuses.CONNECTING);
      await store.dispatch("callsCampaign/updateCampaignId", parsedCampaignId);
      await store.dispatch("calls/startOutgoingCall", {
        details: callDetails,
        isCampaign: true,
      });
      
      await store.dispatch("callsCampaign/refreshCampaignDetails");
      
      return true;
    } catch (error) {
      console.error("[CampaignCallHandler] Error updating store:", error);
      return false;
    }
  }

  async updateStoreOnCampaignAnswer() {
    try {
      await store.dispatch("calls/updateCallStatus", callStatuses.OPEN);
      await store.dispatch("calls/setIsCallConnected", true);
      return true;
    } catch (error) {
      console.error("[CampaignCallHandler] Error updating store on answer:", error);
      return false;
    }
  }

  logAudioDeviceError(error) {
    try {
      if (!error || !error.name) {
        return;
      }

      const errorMessage = audioDeviceErrorMessages[error.name];
      
      if (errorMessage) {
        console.error("[CampaignCallHandler] Audio device error:", errorMessage);
      }
    } catch (err) {
      console.error("[CampaignCallHandler] Error logging audio device error:", err);
    }
  }

  async handleAutoAnswerError(provider, error) {
    try {
      console.error(`[CampaignCallHandler] Auto-answer error for ${provider}:`, error);

      this.logAudioDeviceError(error);

      await store.dispatch("calls/updateCallStatus", callStatuses.CLOSED);
      await store.dispatch("calls/endOutgoingCall");
      await store.dispatch("calls/setIsCallConnected", false);
      await store.dispatch("calls/setIsCampaign", false);

      return true;
    } catch (err) {
      console.error("[CampaignCallHandler] Error handling auto-answer error:", err);
      return false;
    }
  }

  validateCampaignCallParams(callUUID, srcNumber, destEndpoint, campaignId) {
    try {
      if (!callUUID) {
        console.error("[CampaignCallHandler] Missing call UUID");
        return false;
      }

      if (!campaignId) {
        console.error("[CampaignCallHandler] Missing campaign ID");
        return false;
      }

      return true;
    } catch (error) {
      console.error("[CampaignCallHandler] Error validating params:", error);
      return false;
    }
  }

  async requestMicrophonePermission() {
    try {
      await navigator.mediaDevices.getUserMedia({ audio: true });
      return { success: true, error: null };
    } catch (error) {
      console.error("[CampaignCallHandler] Microphone permission error:", error);
      return { success: false, error };
    }
  }

  parseExtraHeaders(extraHeaders) {
    try {
      if (!extraHeaders) {
        return {
          campaignId: null,
          entityId: null,
          entityType: null,
          taskId: null,
          from: null,
          to: null,
          userId: null,
          callerId: null,
        };
      }

      const findHeader = (key) => {
        try {
          const headerKey = Object.keys(extraHeaders).find(
            (k) => k && k.toLowerCase() === key.toLowerCase()
          );
          return headerKey ? extraHeaders[headerKey] : null;
        } catch (error) {
          return null;
        }
      };

      return {
        campaignId: findHeader("x-ph-campaignid"),
        entityId: findHeader("x-ph-associatedentityid") || findHeader("x-ph-idofentity"),
        entityType: findHeader("x-ph-type") || findHeader("x-ph-typeofentity"),
        taskId: findHeader("x-ph-taskid"),
        from: findHeader("x-ph-from"),
        to: findHeader("x-ph-to") || findHeader("to"),
        userId: findHeader("x-ph-userid"),
        callerId: findHeader("x-ph-callerid"),
      };
    } catch (error) {
      console.error("[CampaignCallHandler] Error parsing headers:", error);
      return {
        campaignId: null,
        entityId: null,
        entityType: null,
        taskId: null,
        from: null,
        to: null,
        userId: null,
        callerId: null,
      };
    }
  }
}

export default new CampaignCallHandler();

